import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PortalUserRow } from "../lib/api";
import { useSession } from "../lib/auth";
import { Panel } from "../components/salience/Panel";

/**
 * Settings → Users (admins only).
 *
 * Manages the portal allow-list: who has an account and which of the two
 * assignable roles (Admin, Safety reviewer) they hold. Member is implicit.
 * The proxy and the portal app both enforce admin-only and the lock-out rules
 * (self_lockout / last_admin); the UI disables the obvious foot-guns and maps
 * the backend error codes to plain messages.
 *
 * Dark-terminal register, same as ThemesManager.
 */

const ERROR_MESSAGES: Record<string, string> = {
  exists: "That email already has an account.",
  invalid_email: "That doesn't look like a valid email address.",
  invalid_name: "Please enter a name.",
  invalid_roles: "Those roles aren't valid.",
  invalid_status: "That status isn't valid.",
  self_lockout: "You can't remove, disable or take admin away from yourself.",
  last_admin: "There must always be at least one active admin.",
  forbidden: "Only admins can manage users.",
  not_found: "That user no longer exists.",
};

function friendly(err: unknown): string {
  const code = err instanceof Error ? err.message : String(err);
  return ERROR_MESSAGES[code] ?? `Something went wrong (${code}).`;
}

function formatWhen(iso: string): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Roles we send: the assignable ones only (member is implicit). */
function withRole(roles: string[], role: string, on: boolean): string[] {
  const rest = roles.filter((r) => r !== "member" && r !== role);
  return on ? [...rest, role] : rest;
}

const INPUT_CLASSES =
  "w-full rounded border border-white/15 bg-terminal-panel px-2 py-1 text-sm text-terminal-ink placeholder:text-terminal-ink-muted/60 focus:border-white/40 focus:outline-none";

const PRIMARY_BUTTON =
  "rounded bg-agenda-public/20 px-3 py-1 font-semibold text-[#8ab4ff] transition-colors hover:bg-agenda-public/30 disabled:opacity-40";

const ROLE_LABELS: Array<{ role: string; label: string }> = [
  { role: "admin", label: "Admin" },
  { role: "safety_reviewer", label: "Safety reviewer" },
];

export function SettingsUsers() {
  const session = useSession();
  const me = session.data?.user;
  const isAdmin = !!me?.roles.includes("admin");

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Settings
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">Users</h1>
      </header>
      {isAdmin && me ? (
        <UsersAdmin meEmail={me.email} />
      ) : (
        <Panel title="Users">
          <p className="text-sm text-terminal-ink-muted">
            Only admins can manage users.
          </p>
        </Panel>
      )}
    </div>
  );
}

function UsersAdmin({ meEmail }: { meEmail: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["settings-users"],
    queryFn: () => api.settings.listUsers(),
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["settings-users"] });

  const updateMutation = useMutation({
    mutationFn: api.settings.updateUser,
    onSuccess: invalidate,
  });
  const removeMutation = useMutation({
    mutationFn: api.settings.removeUser,
    onSuccess: invalidate,
  });
  const createMutation = useMutation({
    mutationFn: api.settings.createUser,
    onSuccess: invalidate,
  });

  const users = [...(data?.users ?? [])].sort((a, b) =>
    a.email.localeCompare(b.email),
  );

  return (
    <>
      <Panel title="Roles">
        <ul className="space-y-1.5 text-sm text-terminal-ink-muted">
          <li>
            <span className="font-semibold text-terminal-ink">Member</span>{" "}
            — everyone with an account. Can't be removed.
          </li>
          <li>
            <span className="font-semibold text-terminal-ink">Admin</span> —
            can manage users on this page.
          </li>
          <li>
            <span className="font-semibold text-terminal-ink">
              Safety reviewer
            </span>{" "}
            — sees commenter identity on comments flagged as threat or abuse.
            Separate from Admin.
          </li>
        </ul>
        <p className="mt-3 rounded border border-white/10 bg-terminal-panel p-3 text-sm text-terminal-ink">
          Adding someone here gives them a portal account. They also need an
          Andian sign-in invitation before they can sign in — ask Andian (Ian)
          to invite them. Removing or disabling someone here blocks their
          portal access straight away.
        </p>
      </Panel>

      <AddUserForm
        onSubmit={(body) => createMutation.mutateAsync(body)}
        pending={createMutation.isPending}
      />

      <Panel
        title="Portal users"
        eyebrow={`${users.length} user${users.length === 1 ? "" : "s"}`}
      >
        {isLoading ? (
          <div className="h-32 animate-pulse rounded-lg bg-terminal-panel/50" />
        ) : error ? (
          <p className="text-sm text-down">
            We couldn't load the users right now. Please refresh to try again.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-190 text-left text-sm">
              <thead className="text-[10px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
                <tr>
                  <th className="pb-2 pr-3">User</th>
                  <th className="pb-2 pr-3">Roles</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3">Last sign-in</th>
                  <th className="pb-2 pr-3">Signed in before</th>
                  <th className="pb-2 pr-3">Sessions</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow
                    key={u.email}
                    user={u}
                    isSelf={u.email.toLowerCase() === meEmail.toLowerCase()}
                    onUpdate={(patch) =>
                      updateMutation.mutateAsync({ email: u.email, ...patch })
                    }
                    onRemove={() => removeMutation.mutateAsync(u.email)}
                    busy={updateMutation.isPending || removeMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

const SELF_TIP = "You can't lock yourself out.";

function UserRow({
  user,
  isSelf,
  onUpdate,
  onRemove,
  busy,
}: {
  user: PortalUserRow;
  isSelf: boolean;
  onUpdate: (patch: {
    name?: string;
    roles?: string[];
    status?: "active" | "disabled";
  }) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  busy: boolean;
}) {
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(user.name);

  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setRowError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setRowError(friendly(err));
      return false;
    }
  };

  const disabled = user.status === "disabled";

  return (
    <>
      <tr className="border-t border-white/10 align-top">
        <td className="py-2 pr-3">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={INPUT_CLASSES}
                aria-label={`Name for ${user.email}`}
              />
              <button
                onClick={async () => {
                  if (await run(() => onUpdate({ name: name.trim() }))) {
                    setEditingName(false);
                  }
                }}
                disabled={busy || !name.trim()}
                className={`${PRIMARY_BUTTON} text-xs`}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingName(false);
                  setName(user.name);
                  setRowError(null);
                }}
                className="text-xs text-terminal-ink-muted transition-colors hover:text-terminal-ink"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="font-medium text-terminal-ink">
              {user.name}
              {isSelf && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
                  you
                </span>
              )}
              <button
                onClick={() => setEditingName(true)}
                className="ml-2 text-xs font-normal text-terminal-ink-muted transition-colors hover:text-terminal-ink"
              >
                Edit
              </button>
            </div>
          )}
          <div className="text-xs text-terminal-ink-muted">{user.email}</div>
        </td>
        <td className="py-2 pr-3">
          <div className="flex flex-col gap-1">
            <span className="w-fit rounded bg-white/10 px-2 py-0.5 text-xs text-terminal-ink-muted">
              Member
            </span>
            {ROLE_LABELS.map(({ role, label }) => {
              const lockSelf = isSelf && role === "admin";
              return (
                <label
                  key={role}
                  className="flex items-center gap-2 text-xs text-terminal-ink"
                  title={lockSelf ? SELF_TIP : undefined}
                >
                  <input
                    type="checkbox"
                    checked={user.roles.includes(role)}
                    disabled={busy || lockSelf}
                    onChange={(e) =>
                      void run(() =>
                        onUpdate({
                          roles: withRole(user.roles, role, e.target.checked),
                        }),
                      )
                    }
                  />
                  {label}
                </label>
              );
            })}
          </div>
        </td>
        <td className="py-2 pr-3">
          <button
            onClick={() =>
              void run(() =>
                onUpdate({ status: disabled ? "active" : "disabled" }),
              )
            }
            disabled={busy || isSelf}
            title={isSelf ? SELF_TIP : undefined}
            className={`rounded px-2 py-0.5 text-xs font-semibold transition-colors disabled:opacity-40 ${
              disabled
                ? "bg-down/15 text-down hover:bg-down/25"
                : "bg-up/15 text-up hover:bg-up/25"
            }`}
          >
            {disabled ? "Disabled" : "Active"}
          </button>
        </td>
        <td className="py-2 pr-3 text-xs text-terminal-ink-muted">
          {formatWhen(user.lastSignInAt)}
        </td>
        <td className="py-2 pr-3 text-xs text-terminal-ink-muted">
          {user.bound ? "Yes" : "No"}
        </td>
        <td className="py-2 pr-3 text-xs text-terminal-ink-muted">
          {user.activeSessions}
        </td>
        <td className="py-2 text-right text-xs">
          {confirming ? (
            <span className="inline-flex items-center gap-2">
              <button
                onClick={async () => {
                  if (!(await run(onRemove))) setConfirming(false);
                }}
                disabled={busy}
                className="rounded bg-down/15 px-2 py-0.5 font-semibold text-down transition-colors hover:bg-down/25 disabled:opacity-40"
              >
                Confirm remove
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="text-terminal-ink-muted transition-colors hover:text-terminal-ink"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              disabled={busy || isSelf}
              title={isSelf ? SELF_TIP : undefined}
              className="text-terminal-ink-muted transition-colors hover:text-down disabled:opacity-40 disabled:hover:text-terminal-ink-muted"
            >
              Remove
            </button>
          )}
        </td>
      </tr>
      {rowError && (
        <tr>
          <td colSpan={7} className="pb-2 text-xs text-down">
            {rowError}
          </td>
        </tr>
      )}
    </>
  );
}

function AddUserForm({
  onSubmit,
  pending,
}: {
  onSubmit: (body: {
    email: string;
    name: string;
    roles: string[];
  }) => Promise<unknown>;
  pending: boolean;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await onSubmit({ email: email.trim(), name: name.trim(), roles });
      setEmail("");
      setName("");
      setRoles([]);
    } catch (err) {
      setError(friendly(err));
    }
  };

  return (
    <Panel title="Add a user">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="block text-xs font-medium text-terminal-ink-muted">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 ${INPUT_CLASSES}`}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-terminal-ink-muted">
              Name
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 ${INPUT_CLASSES}`}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-4">
          {ROLE_LABELS.map(({ role, label }) => (
            <label
              key={role}
              className="flex items-center gap-2 text-sm text-terminal-ink"
            >
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={(e) => setRoles(withRole(roles, role, e.target.checked))}
              />
              {label}
            </label>
          ))}
        </div>
        {error && <p className="text-xs text-down">{error}</p>}
        <button
          onClick={submit}
          disabled={pending || !email.trim() || !name.trim()}
          className={`${PRIMARY_BUTTON} text-sm`}
        >
          {pending ? "Adding…" : "Add"}
        </button>
      </div>
    </Panel>
  );
}
